/**
 * m3 echo Frontend - Quota Bar
 *
 * A meter for the monthly autopilot quota: one horizontal bar whose full width
 * is the monthly limit, split into the segments that make it up.
 *
 * Color is an *ordinal* one-hue ramp, not a categorical palette. The segments
 * are not independent identities — they are stages of commitment against a
 * single magnitude (booked -> consumed -> free), so they take monotone steps of
 * the theme's own primary ramp and the reader sees the order in the color.
 * Four categorical hues here would spend the identity channel on something bar
 * length already shows. The three steps are the theme's `primary.dark` / `main`
 * / `light` verbatim: monotone in OKLCH lightness (0.513 / 0.619 / 0.706), every
 * adjacent gap above the 0.06 floor, one hue (3 deg spread), and the pale end
 * still at 2.60:1 against the white card.
 *
 * A preview segment is not yet committed, so it does not get a ramp step at
 * all — it is carried by texture (a 45 deg hatch) plus an outline, which keeps
 * "proposed" legible even where hue is not. Over-limit is a *state*, not a
 * series, so it wears the reserved status red and always ships with an icon and
 * a worded label.
 */

import React from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

export interface QuotaSegment {
  key: string;
  value: number;
  label: string;
  variant: 'reserved' | 'consumed' | 'preview' | 'free';
}

interface Props {
  limit: number;
  segments: QuotaSegment[];
  label: string;
  compact?: boolean;
  /**
   * The data value that maps to the component's full width. Omit and the bar
   * scales to itself (`max(limit, sum of segments)`), which is right for a bar
   * standing alone. Pass it to put several bars on one scale — two stacked
   * meters that both say "390" must put their limit rules at the same x, or the
   * reader compares widths that are not comparable. `MonthlyQuotaPanel` does
   * exactly that. It can only ever *widen* a bar: a value below what this bar
   * needs is ignored, so a caller can never push a segment outside the box.
   */
  scale?: number;
}

/**
 * The ordinal ramp.
 *
 * These hexes are the theme's `primary.dark` / `main` / `light`
 * (`src/index.tsx`) copied verbatim rather than read through `useTheme()`,
 * which is already in scope. That is deliberate: the three steps were *checked*
 * as a ramp — monotone OKLCH lightness with every adjacent gap over the 0.06
 * floor, one hue, and a pale end that still clears the surface — and reading
 * them live would let a future theme edit silently break a property nothing in
 * the app re-tests. If the theme's primary ramp changes, re-run the ordinal
 * validation against the new steps and update these constants deliberately.
 *
 * Also deliberately light-only: every ratio quoted here is against the white
 * card. The app ships no dark theme and no `prefers-color-scheme` handling, so
 * a dark ramp would be dead code. If dark mode ever lands, these steps need
 * re-stepping for the dark surface and re-validating — not an automatic flip.
 *
 * Known and accepted: `free` at 2.60:1 against the white card is 2.27:1 against
 * the `#EDF0F3` track, under WCAG 1.4.11's 3:1 for graphical objects. It is
 * never the only carrier — every segment's value is always printed as text
 * beside it — which is the documented relief for a sub-threshold fill.
 *
 * Exported so later views reuse these steps rather than inventing their own.
 * Booked is the darkest step because it renders leftmost and is the firmest
 * commitment; lightness then decreases to the right, which is what makes the
 * bar read as one ramp instead of four arbitrary colors.
 */
export const QUOTA_RAMP = {
  reserved: '#4D6B80', // theme primary.dark
  consumed: '#678BA3', // theme primary.main
  free: '#8AA4B8', // theme primary.light
} as const;

/**
 * Status critical - reserved for the over-limit state, never for a segment.
 * Exported so everything that reports an overrun uses one red; it clears
 * 4.80:1 on the white card, where the theme's own `error.main` (#f44336,
 * 3.68:1) would fail body-text contrast.
 */
export const QUOTA_OVER_LIMIT_INK = '#d03b3b';
/** Chart chrome: the empty track and the limit rule. Not data. */
const TRACK_FILL = '#EDF0F3';
const LIMIT_RULE_INK = '#5A6C7D';

const BAR_HEIGHT = 12;
const COMPACT_BAR_HEIGHT = 8;
const END_RADIUS = 4;
/** White between touching fills - the separator is the gap, never a border. */
const SURFACE_GAP = 2;

const hatchFill = (ink: string) =>
  `repeating-linear-gradient(45deg, ${ink} 0 2px, rgba(255,255,255,0) 2px 5px)`;

/**
 * Every number entering the component passes through here, so a bad value can
 * never make the marks and the text disagree. `Math.max(0, NaN)` is `NaN`,
 * which slips past a `<= 0` guard and then poisons the running offset, the
 * scale and the overflow test at once (`NaN > 0` is false, so nothing warns).
 *
 * Exported so a caller computing a shared `scale` clamps identically - two
 * guards written separately are how marks and labels drift apart.
 */
export const safeValue = (value: number) => (Number.isFinite(value) ? Math.max(0, value) : 0);

/**
 * How a segment is painted. `over` marks the part of a segment that runs past
 * the limit; it keeps the segment's texture but takes the status ink, so an
 * over-limit preview still reads as a preview.
 */
const fillFor = (variant: QuotaSegment['variant'], over: boolean): React.CSSProperties => {
  const ink = over ? QUOTA_OVER_LIMIT_INK : QUOTA_RAMP[variant === 'preview' ? 'reserved' : variant];
  if (variant === 'preview') {
    return {
      backgroundColor: '#ffffff',
      backgroundImage: hatchFill(ink),
      boxShadow: `inset 0 0 0 1px ${ink}`,
    };
  }
  return { backgroundColor: ink };
};

interface LaidSegment {
  segment: QuotaSegment;
  start: number;
  /** The part of the segment that fits inside the limit. */
  inSize: number;
  /** The part that runs past it. Non-zero means this segment overran. */
  overSize: number;
}

/**
 * Lay the segments end to end and record where each one crosses `limit`, so
 * the part that does not fit can be painted as over-limit even when it is the
 * middle of a segment. Nothing is clipped: the component's width covers the
 * whole extent, so overflow renders past the limit rule instead of vanishing.
 *
 * Expects values already through `safeValue` - it does no clamping of its own,
 * because a second, differently-written guard is how the marks and the labels
 * drift apart.
 */
const layOut = (segments: QuotaSegment[], limit: number): LaidSegment[] => {
  let cursor = 0;
  return segments.map((segment) => {
    const start = cursor;
    const end = start + segment.value;
    const boundary = Math.min(end, Math.max(limit, start));
    cursor = end;
    return { segment, start, inSize: boundary - start, overSize: end - boundary };
  });
};

export const QuotaBar: React.FC<Props> = ({ limit, segments, label, compact = false, scale }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const safeLimit = safeValue(limit);
  const safeSegments = segments.map((segment) => ({ ...segment, value: safeValue(segment.value) }));

  const total = safeSegments.reduce((sum, segment) => sum + segment.value, 0);
  const overflow = Math.max(0, total - safeLimit);
  /**
   * The data value mapped to 100% width: what this bar needs, widened to a
   * caller's shared scale when one is given. Clamped rather than trusted, so a
   * shared scale can never be smaller than the content it has to hold.
   */
  const extent = Math.max(safeLimit, total, safeValue(scale ?? 0));
  const divisor = extent > 0 ? extent : 1;
  const toPct = (value: number) => (value / divisor) * 100;
  const limitPct = extent > 0 ? toPct(safeLimit) : 100;

  // In bar order. Position is no longer carried on a piece: the row below lays
  // them out in flow, so where a piece sits is where the ones before it end.
  const laid = layOut(safeSegments, safeLimit);
  const pieces = laid.flatMap(({ segment, inSize, overSize }) => [
    ...(inSize > 0
      ? [{ id: `${segment.key}-in`, variant: segment.variant, size: inSize, over: false }]
      : []),
    ...(overSize > 0
      ? [{ id: `${segment.key}-over`, variant: segment.variant, size: overSize, over: true }]
      : []),
  ]);

  /**
   * The compact readout, segment by segment in the order the bar draws them,
   * then the limit: "12/40/400" is twelve booked and forty consumed out of
   * four hundred. A preview gets a figure of its own deliberately - the
   * compact bar draws the preview segment and its overrun, so folding it into
   * a neighbour would print a number that contradicts the marks beside it.
   *
   * The figures carry no words and no colour of their own. Two of the three
   * ramp steps fail small-text contrast on the card (`consumed` sits at
   * 3.62:1), and repeating labels on every row of a table is noise: the caller
   * names the order once in its column header and spells it out in full in the
   * hover title.
   */
  const committed = safeSegments.filter((segment) => segment.variant !== 'free');
  const compactReadout = [
    // A bar with no segments still reads as a fraction, not as a bare limit.
    ...(committed.length > 0 ? committed.map((segment) => segment.value) : [0]),
    safeLimit,
  ].join('/');

  const height = compact ? COMPACT_BAR_HEIGHT : BAR_HEIGHT;
  const breakdown = safeSegments.map((segment) => `${segment.label} ${segment.value}`).join(', ');
  // Built by joining only the parts that exist, so an empty bar reads
  // "Label: Limit 0" rather than trailing a comma into nothing.
  const barLabel = [
    breakdown,
    `Limit ${safeLimit}`,
    overflow > 0 ? `${overflow} über dem Limit` : '',
  ]
    .filter(Boolean)
    .join(', ');
  const accessibleName = `${label}: ${barLabel}`;

  const bar = (
    <Box
      role="img"
      aria-label={accessibleName}
      sx={{ position: 'relative', width: '100%', height, minWidth: 48 }}
    >
      {/* Track: chrome, drawn only as far as the limit, so its right edge is the limit. */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: `${limitPct}%`,
          height: '100%',
          backgroundColor: TRACK_FILL,
          borderRadius: `${END_RADIUS}px`,
        }}
      />
      {/*
        Laid out in flow, not each piece pinned at its own data x. The 3px
        floor below is a promise that a non-zero segment stays visible, and
        absolute positioning broke it the moment a segment fell under 3px: the
        next piece's `left` is its data position, which lands *inside* those
        3px, and the later piece — painted later, with no z-order between
        them — covered the floor back up. The value survived in the legend and
        the aria-label and nowhere on the bar. A flex row is the only place
        that comparison can be resolved, because whether a percentage is under
        3px is not known until layout: a floored piece pushes its neighbours
        along instead of being painted over by them.

        Deferred, and quantified so the eventual fix is not under-scoped: the
        floors drift the flow against everything still pinned at a data x.
        Each non-last piece occupies max(3px, pct% - 2px) + 2px (its basis
        floored, plus the gap put back), so any piece under 5px of data width
        inflates to 5px of occupancy — and up to three pieces (the in-limit
        parts of reserved, consumed and preview) can precede the limit
        crossing, which puts the visible crossing up to ~15px right of
        limitPct in the worst case, not 3px. The mismatch shows against the
        always-drawn track, whose right edge sits at limitPct — not only
        against the limit rule, which is drawn just on overflow. Fixing it
        means taking the boundary into the flow as well (a zero-width flex
        child at the limit); `layOut` already splits every segment at that
        boundary, so the pieces for it exist.
      */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'stretch',
        }}
      >
        {pieces.map((piece, index) => {
          const isLast = index === pieces.length - 1;
          return (
            <Box
              key={piece.id}
              sx={{
                // Basis is the data width; `minWidth` is the visibility floor,
                // and `flexShrink: 0` keeps the floor from being negotiated
                // away again. A bar whose floors together exceed its box grows
                // past it rather than dropping a segment - the trade this
                // floor exists to make.
                flexGrow: 0,
                flexShrink: 0,
                flexBasis: isLast
                  ? `${toPct(piece.size)}%`
                  : `calc(${toPct(piece.size)}% - ${SURFACE_GAP}px)`,
                minWidth: '3px',
                marginRight: isLast ? 0 : `${SURFACE_GAP}px`,
                borderTopLeftRadius: index === 0 ? END_RADIUS : 0,
                borderBottomLeftRadius: index === 0 ? END_RADIUS : 0,
                borderTopRightRadius: isLast ? END_RADIUS : 0,
                borderBottomRightRadius: isLast ? END_RADIUS : 0,
                ...fillFor(piece.variant, piece.over),
              }}
            />
          );
        })}
      </Box>
      {/* The limit rule only earns its ink when something runs past it. */}
      {overflow > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: -3,
            height: 'calc(100% + 6px)',
            left: `${limitPct}%`,
            width: '2px',
            marginLeft: '-1px',
            backgroundColor: LIMIT_RULE_INK,
            borderRadius: '1px',
          }}
        />
      )}
    </Box>
  );

  if (compact) {
    return (
      // No `title` here. The compact bar is meant to be wrapped by whatever
      // explains it - `TenantTable` puts a MUI `Tooltip` around it - and a
      // native tooltip underneath produced two hover texts at once, worded
      // differently, one browser-styled and one themed. The bar's own text is
      // on the `aria-label` inside, where assistive technology reads it; the
      // hover text is the caller's to own.
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 150 }}>
        <Box sx={{ flex: 1, minWidth: 48 }}>{bar}</Box>
        <Typography
          variant="caption"
          sx={{
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
            color: overflow > 0 ? QUOTA_OVER_LIMIT_INK : 'text.secondary',
            fontWeight: overflow > 0 ? 600 : 400,
          }}
        >
          {compactReadout}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1,
          mb: 0.75,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {safeLimit}
        </Typography>
      </Box>

      {bar}

      {/* Every segment named and valued in text - the meter never leans on color. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          flexWrap: 'wrap',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? 0.5 : 2,
          mt: 1,
        }}
      >
        {laid.map(({ segment, inSize, overSize }) => (
          <Box key={segment.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '2px',
                flexShrink: 0,
                // A segment lying wholly past the limit is drawn entirely in the
                // status ink, so its swatch has to follow - otherwise the key
                // points at a color that is nowhere in the bar. One that merely
                // straddles keeps its own step, which is still on screen.
                ...fillFor(segment.variant, inSize <= 0 && overSize > 0),
              }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {segment.label}{' '}
              <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {segment.value}
              </Box>
            </Typography>
          </Box>
        ))}
      </Box>

      {overflow > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
          <WarningAmberIcon sx={{ fontSize: 16, color: QUOTA_OVER_LIMIT_INK, flexShrink: 0 }} />
          <Typography variant="caption" sx={{ color: QUOTA_OVER_LIMIT_INK, fontWeight: 600 }}>
            {overflow} Prompts über dem Limit von {safeLimit}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default QuotaBar;
